import React, { FC, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ScheduleSettings } from './ScheduleSettings';
import { SettingsComingSoon, SettingsOptionInfo } from './SettingsComingSoon';
import './TrainerSettings.scss';

export const SETTINGS_OPTIONS: SettingsOptionInfo[] = [
  {
    id: 'account-details',
    label: 'Account details',
    description: 'Manage your personal information, profile identification, and contact info.',
    icon: 'user',
  },
  {
    id: 'email',
    label: 'Email',
    description: 'Update your login email address, notification emails, and delivery settings.',
    icon: 'email',
  },
  {
    id: 'password-security',
    label: 'Password & Security',
    description: 'Manage your password, two-factor authentication (2FA), and active logins.',
    icon: 'lock',
  },
  {
    id: 'notifications',
    label: 'Notifications',
    description:
      'Configure in-app, email, and push notification preferences for client activities.',
    icon: 'bell',
  },
  {
    id: 'schedule',
    label: 'Schedule',
    description: 'Manage your working hours, blocked dates, and automated AI responses.',
    icon: 'schedule',
  },
  {
    id: 'privacy-data',
    label: 'Privacy & Data',
    description: 'Control your data sharing preferences, analytics consent, and export your data.',
    icon: 'shield',
  },
  {
    id: 'business-payments',
    label: 'Business & Payments',
    description: 'View your Stripe Connect payout account, invoice history, and banking details.',
    icon: 'card',
  },
  {
    id: 'delete-account',
    label: 'Delete Account',
    description: 'Permanently remove your trainer account, client associations, and personal data.',
    icon: 'trash',
  },
];

export const resolveSettingsSectionId = (rawQuery: string | null | undefined): string => {
  if (!rawQuery) return 'schedule';

  const cleaned = rawQuery
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-');

  // Exact ID match
  const exact = SETTINGS_OPTIONS.find((opt) => opt.id === cleaned);
  if (exact) return exact.id;

  // Flexible alias matching
  const aliases: Record<string, string> = {
    account: 'account-details',
    'account-detail': 'account-details',
    accountdetails: 'account-details',
    'account-details': 'account-details',
    email: 'email',
    password: 'password-security',
    security: 'password-security',
    'password-security': 'password-security',
    passwordsecurity: 'password-security',
    notification: 'notifications',
    notifications: 'notifications',
    schedule: 'schedule',
    schedules: 'schedule',
    privacy: 'privacy-data',
    data: 'privacy-data',
    'privacy-data': 'privacy-data',
    privacydata: 'privacy-data',
    business: 'business-payments',
    payments: 'business-payments',
    payment: 'business-payments',
    'business-payments': 'business-payments',
    businesspayments: 'business-payments',
    delete: 'delete-account',
    'delete-account': 'delete-account',
    deleteaccount: 'delete-account',
  };

  if (aliases[cleaned]) {
    return aliases[cleaned];
  }

  // Label match
  const labelMatch = SETTINGS_OPTIONS.find(
    (opt) => opt.label.toLowerCase() === rawQuery.trim().toLowerCase(),
  );
  if (labelMatch) return labelMatch.id;

  return 'schedule';
};

export const TrainerSettings: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const rawSectionParam =
    searchParams.get('section') ||
    searchParams.get('subtab') ||
    searchParams.get('subTab') ||
    searchParams.get('setting') ||
    (tabParam !== 'settings' ? tabParam : null);

  const activeSectionId = resolveSettingsSectionId(rawSectionParam);

  // Synchronize URL with canonical query: ?tab=settings&section=<sectionId>
  useEffect(() => {
    const currentTab = searchParams.get('tab');
    const currentSection = searchParams.get('section');

    // If on settings tab or arrived via section query, ensure both tab=settings and section are present
    if (currentTab === 'settings' || !currentTab) {
      if (currentSection !== activeSectionId) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set('tab', 'settings');
            next.set('section', activeSectionId);
            next.delete('subtab');
            next.delete('subTab');
            next.delete('setting');
            return next;
          },
          { replace: true },
        );
      }
    }
  }, [searchParams, setSearchParams, activeSectionId]);

  const handleSelectSection = (sectionId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'settings');
      next.set('section', sectionId);
      next.delete('subtab');
      next.delete('subTab');
      next.delete('setting');
      return next;
    });
  };

  const currentOption =
    SETTINGS_OPTIONS.find((opt) => opt.id === activeSectionId) || SETTINGS_OPTIONS[4];

  return (
    <div className="trainer-settings-container">
      {/* Two Column Layout */}
      <div className="trainer-settings-layout">
        {/* Left Sidebar Navigation */}
        <aside className="trainer-settings-sidebar" aria-label="Settings navigation">
          <nav className="settings-nav-list">
            {SETTINGS_OPTIONS.map((option) => {
              const isActive = option.id === activeSectionId;
              const isDelete = option.id === 'delete-account';

              return (
                <button
                  key={option.id}
                  type="button"
                  className={`settings-nav-item ${isActive ? 'active' : ''} ${
                    isDelete ? 'delete-item' : ''
                  }`}
                  onClick={() => handleSelectSection(option.id)}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <span className="nav-item-label">{option.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Right Main Content Panel */}
        <main className="trainer-settings-main-content">
          {activeSectionId === 'schedule' ? (
            <ScheduleSettings />
          ) : (
            <SettingsComingSoon option={currentOption} />
          )}
        </main>
      </div>
    </div>
  );
};
